/** One atomic persisted credential pair, shared by dashboard login and mobile handoff. */
export interface SessionUser { id: string; email: string; role: string; displayName?: string }
export interface Session { accessToken: string; refreshToken?: string; user?: SessionUser }
export const SESSION_KEY = 'appbuilder.session';
const LEGACY_ACCESS_KEY = 'appbuilder.accessToken';
let memory: Session | null = null;

export function readSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw) {
      const value = JSON.parse(raw) as Session;
      return typeof value.accessToken === 'string' && value.accessToken ? value : null;
    }
    const accessToken = localStorage.getItem(LEGACY_ACCESS_KEY);
    return accessToken ? { accessToken } : null;
  } catch { return memory; }
}

export function writeSession(session: Session | null): void {
  memory = session;
  try {
    if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(LEGACY_ACCESS_KEY);
    if (!session) localStorage.removeItem('appbuilder.deviceId');
  } catch { /* Browser storage unavailable: keep the current session in memory. */ }
}

export function sameCredentials(a: Session | null, b: Session | null): boolean {
  return a?.accessToken === b?.accessToken && a?.refreshToken === b?.refreshToken;
}

/** One renewal per provider, with guards against logout or account changes in any tab. */
export function createSessionRenewer(
  current: () => Session | null,
  save: (session: Session) => void,
  refresh: (credential: string) => Promise<{ tokens: { accessToken: string; refreshToken: string } }>,
) {
  let inFlight: Promise<boolean> | null = null;
  return async (staleToken: string | null, staleRefresh: string | null): Promise<boolean> => {
    const start = current();
    if (!start) return false;
    if (start.accessToken !== staleToken) return !!staleRefresh && start.refreshToken === staleRefresh;
    if (!start.refreshToken) return false;
    if (inFlight) return inFlight;
    const pending = (async () => {
      const result = await refresh(start.refreshToken!);
      if (!sameCredentials(current(), start) || !sameCredentials(readSession(), start)) return false;
      save({ ...start, accessToken: result.tokens.accessToken, refreshToken: result.tokens.refreshToken });
      return true;
    })();
    inFlight = pending;
    try { return await pending; }
    finally { if (inFlight === pending) inFlight = null; }
  };
}
